import React, { useEffect, useRef, useState } from 'react';
import HeroDemo from './HeroDemo';
import { ArrowRight } from 'lucide-react';

const STATES = [
  {
    line1: 'Hours of Audio.',
    line2: 'Moments in Minutes.',
    line3: 'Insights in Seconds.',
  },
  {
    line1: 'Every recording hides',
    line2: 'a moment worth sharing.',
  },
];

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef(null);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx(i => (i + 1) % STATES.length), 7000);
    return () => clearInterval(t);
  }, [paused]);

  const smoothTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 40) {
      setIdx(i => (i + (dx < 0 ? 1 : STATES.length - 1)) % STATES.length);
    }
    touchStart.current = null;
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-14 pb-16 sm:pt-20 sm:pb-24"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[55%_45%] gap-10 lg:gap-14 items-center">
          <div
            className="reveal"
            role="group"
            aria-label="Hero message"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            onFocus={() => setPaused(true)}
            onBlur={() => setPaused(false)}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <span className="pill mb-6" data-testid="hero-eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-[#7EE06D] shadow-[0_0_8px_#7EE06D]" />
              Powered by SIMTECH AI
            </span>

            <div className="relative min-h-[260px] sm:min-h-[300px]">
              {STATES.map((s, i) => (
                <h1
                  key={i}
                  aria-hidden={i !== idx}
                  className={`font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight absolute inset-0 transition-opacity duration-700 ${
                    i === idx ? 'opacity-100 relative' : 'opacity-0 pointer-events-none'
                  }`}
                >
                  <span className="block text-white/90">{s.line1}</span>
                  <span className={`block ${s.line3 ? 'text-grad-sweep-opp' : 'text-grad-sweep'}`}>{s.line2}</span>
                  {s.line3 && (
                    <span className="block text-grad-sweep">{s.line3}</span>
                  )}
                </h1>
              ))}
            </div>

            <p className="text-white/60 text-base sm:text-lg max-w-xl mt-6">
              SIMNOPSIS listens to your recording and surfaces the moments worth
              sharing — with waveform, timestamp, rating, theme, and quote.
              No more scrubbing through hours of audio.
            </p>

            <div className="flex flex-wrap gap-3 mt-8">
              <button
                onClick={() => smoothTo('upload')}
                className="btn-gradient inline-flex items-center gap-2"
                data-testid="hero-cta-primary"
              >
                Start finding highlights <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => smoothTo('use-cases')}
                className="btn-ghost"
                data-testid="hero-cta-secondary"
              >
                Try a sample instead
              </button>
            </div>

            {/* Dot indicators */}
            <div className="flex gap-2 mt-6" role="tablist" aria-label="Headline states">
              {STATES.map((_, i) => (
                <button
                  key={i}
                  aria-current={i === idx ? 'true' : undefined}
                  aria-label={`Headline ${i + 1}`}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === idx ? 'w-8 bg-white/80' : 'w-2 bg-white/25'
                  }`}
                />
              ))}
            </div>

            <div className="mt-8 font-mono text-[11px] tracking-[0.14em] uppercase text-white/40">
              MP3 · WAV · M4A · MP4 · MOV · Links — Up to 2 hours
            </div>
          </div>

          <div className="reveal">
            <HeroDemo />
          </div>
        </div>
      </div>
    </section>
  );
}
